import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, IconButton, Typography
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  CartesianGrid, ComposedChart, Legend, Line,
  ResponsiveContainer, Tooltip, XAxis, YAxis
} from "recharts";
import {
  fetchConversationTimeData,
  fetchConversationTimeConversations,
} from "../../../services/dashboardModule/dashboardServices.js";
import DatePickerComponent from "../../../common/components/DatePickerComponent.jsx";
import ConversationDrillDownDialog from "../../../components/ConversationDrillDownDialog";
import { STRINGS } from "../../../common/constants/commonConstants.js";
import { formatDateTime } from "../../../common/constants/dashboardModule/conversationTimeConstants.js";

// ── Constants ──────────────────────────────────────────────────────
const BLUE   = "#378ADD";
const GREEN  = "#1D9E75";
const AMBER  = "#BA7517";
const PINK   = "#D4537E";
const PURPLE = "#7F77DD";

const EXPORT_COLS = [
  { key: "ConversationDate",       label: "Date", isDate: true },
  { key: "AvgFirstResponseInMins", label: "Avg 1st Resp (m)" },
  { key: "AvgFirstResponseInHrs",  label: "Avg 1st Resp (h)" },
  { key: "AvgConversationInMins",  label: "Avg Conv (m)"     },
  { key: "AvgConversationInHrs",   label: "Avg Conv (h)"     },
  { key: "MinFirstResponseInMins", label: "Min 1st Resp (m)" },
  { key: "MaxFirstResponseInMins", label: "Max 1st Resp (m)" },
  { key: "MinConversationInMins",  label: "Min Conv (m)"     },
  { key: "MaxConversationInMins",  label: "Max Conv (m)"     },
];

// ── Helpers ────────────────────────────────────────────────────────
function fmtDate(val) {
  if (!val) return "";
  const d = new Date(val);
  if (isNaN(d)) return val;
  return d.toLocaleDateString("en-US", {
    month: "2-digit", day: "2-digit", year: "2-digit",
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
}

function safeNum(v, dec = 2) {
  const n = Number(v);
  if (isNaN(n)) return "—";
  return n.toFixed(dec);
}

function safeVal(v) {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
}

const csvEscape = (v) => {
  if (v == null) return "";
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// ── Sub-components ─────────────────────────────────────────────────
function KpiCard({ label, value, sub, color = BLUE, onClick }) {
  return (
    <Box onClick={onClick} sx={{
      background: "#f7f7f7", borderRadius: 2,
      p: "10px 14px", minWidth: 0, flex: 1,
      cursor: onClick ? "pointer" : "default",
      transition: "background .15s",
      "&:hover": onClick ? { background: "#efefef" } : {},
    }}>
      <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 500, color, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.25 }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, fontWeight: 500, color: "text.secondary",
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
    }}>
      {children}
    </Typography>
  );
}

function ChartCard({ title, children, height = 200 }) {
  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, p: "12px 14px" }}>
      {title && (
        <Typography sx={{
          fontSize: 11, fontWeight: 500, color: "text.secondary",
          textTransform: "uppercase", letterSpacing: "0.05em", mb: 1,
        }}>
          {title}
        </Typography>
      )}
      <Box sx={{ width: "100%", height }}>{children}</Box>
    </Box>
  );
}

// ── Main Component ─────────────────────────────────────────────────
const ConversationTime = () => {
  const today        = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [startDate,            setStartDate]            = useState(firstOfMonth);
  const [endDate,              setEndDate]              = useState(today);
  const [conversationTimeType, setConversationTimeType] = useState("Texts");
  const [data,                 setData]                 = useState([]);
  const [loading,              setLoading]              = useState(false);
  const [error,                setError]                = useState("");
  const [drill,                setDrill]                = useState(null);

  const load = useCallback(async (start, end, type) => {
    setLoading(true);
    setError("");
    try {
      const result = await fetchConversationTimeData({
        startDate:            formatDateTime(start),
        endDate:              formatDateTime(end),
        conversationTimeType: type,
      });
      setData(result || []);
    } catch (e) {
      console.error(e);
      setError("Failed to load conversation time data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(firstOfMonth, today, "Texts");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── KPIs ───────────────────────────────────────────────────────
  // Track which row (date) produced each min/max so the drill can
  // jump straight to that specific day.
  const kpis = useMemo(() => {
    if (!data.length) return {};
    const avgFirstResp = data.reduce((a,b) => a + safeVal(b.AvgFirstResponseInMins), 0) / data.length;
    const avgConv      = data.reduce((a,b) => a + safeVal(b.AvgConversationInMins),  0) / data.length;

    let minFirstRow = data[0], maxFirstRow = data[0];
    let minConvRow  = data[0], maxConvRow  = data[0];
    data.forEach(r => {
      if (safeVal(r.MinFirstResponseInMins) < safeVal(minFirstRow.MinFirstResponseInMins)) minFirstRow = r;
      if (safeVal(r.MaxFirstResponseInMins) > safeVal(maxFirstRow.MaxFirstResponseInMins)) maxFirstRow = r;
      if (safeVal(r.MinConversationInMins)  < safeVal(minConvRow.MinConversationInMins))   minConvRow  = r;
      if (safeVal(r.MaxConversationInMins)  > safeVal(maxConvRow.MaxConversationInMins))   maxConvRow  = r;
    });

    return {
      avgFirstResp, avgConv,
      minFirst: safeVal(minFirstRow.MinFirstResponseInMins), minFirstRow,
      maxFirst: safeVal(maxFirstRow.MaxFirstResponseInMins), maxFirstRow,
      minConv:  safeVal(minConvRow.MinConversationInMins),   minConvRow,
      maxConv:  safeVal(maxConvRow.MaxConversationInMins),   maxConvRow,
    };
  }, [data]);

  // ── Chart data ─────────────────────────────────────────────────
  const chartData = useMemo(() =>
    [...data]
      .sort((a,b) => new Date(a.ConversationDate) - new Date(b.ConversationDate))
      .map(r => ({
        date:          fmtDate(r.ConversationDate),
        avgFirstMins:  safeVal(r.AvgFirstResponseInMins),
        avgConvMins:   safeVal(r.AvgConversationInMins),
        minFirstMins:  safeVal(r.MinFirstResponseInMins),
        maxFirstMins:  safeVal(r.MaxFirstResponseInMins),
        minConvMins:   safeVal(r.MinConversationInMins),
        maxConvMins:   safeVal(r.MaxConversationInMins),
      }))
  , [data]);

  // ── Drill-down ─────────────────────────────────────────────────
  // date provided → conversations on that date
  // date null     → all conversations in the picker range
  // metric        → optional chip telling the user *why* they're here
  //                 (e.g. "Max conversation: 47.21m")
  const openDrill = useCallback(({ date = null, title, metric = null } = {}) => {
    const resolvedTitle = title
      ?? (date
          ? `Conversations on ${fmtDate(date)}`
          : "All conversations in range");

    setDrill({
      title: resolvedTitle,
      metric,
      context: [
        date && { label: "Date", value: fmtDate(date) },
        !date && startDate && { label: "From", value: fmtDate(startDate) },
        !date && endDate   && { label: "To",   value: fmtDate(endDate)   },
      ].filter(Boolean),
      channel: conversationTimeType,
      fetchData: () => fetchConversationTimeConversations({
        startDate: date
          ? new Date(date).toISOString()
          : (startDate ? startDate.toISOString() : null),
        endDate: date
          ? new Date(date).toISOString()
          : (endDate ? endDate.toISOString() : null),
        conversationTimeType,
      }),
    });
  }, [startDate, endDate, conversationTimeType]);

  // ── CSV export ─────────────────────────────────────────────────
  const exportDisabled = loading || data.length === 0;

  const exportCsv = () => {
    if (!data.length) return;
    const headers = EXPORT_COLS.map(c => csvEscape(c.label));
    const rows = data.map(row =>
      EXPORT_COLS.map(col =>
        col.isDate ? csvEscape(fmtDate(row[col.key])) : csvEscape(row[col.key])
      ).join(",")
    );
    const csv  = "\uFEFF" + [headers.join(","), ...rows].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);

    const now = new Date();
    const ts  = `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,"0")}${String(now.getDate()).padStart(2,"0")}`;
    const channel = conversationTimeType.toLowerCase();

    const a = document.createElement("a");
    a.href     = url;
    a.download = `convtime_${channel}_${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const axisStyle    = { fontSize: 10, fill: "#888" };
  const tooltipStyle = { fontSize: 12 };

  // ── Table columns (drill icon + 9 data columns) ────────────────
  const COLS    = "40px 100px 100px 100px 110px 110px 110px 110px 110px 110px";
  const HEADERS = [
    "",  // drill icon col
    "Date",
    "Avg 1st Resp (m)", "Avg 1st Resp (h)",
    "Avg Conv (m)",     "Avg Conv (h)",
    "Min 1st Resp (m)", "Max 1st Resp (m)",
    "Min Conv (m)",     "Max Conv (m)",
  ];

  return (
    <Box sx={{ p: 0 }}>

      {/* ── Filters ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2,
        alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <DatePickerComponent label={STRINGS.START_DATE} date={startDate} onDateChange={setStartDate} marginRight={0} />
        <DatePickerComponent label={STRINGS.END_DATE}   date={endDate}
          onDateChange={d => { if (d >= startDate) setEndDate(d); }} marginRight={0} />
        <Box onClick={() => load(startDate, endDate, conversationTimeType)} sx={{
          px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
          cursor: "pointer", userSelect: "none",
          background: BLUE, color: "#fff", fontWeight: 500,
          alignSelf: "flex-end", mb: "1px",
          "&:hover": { background: "#2a6db5" },
        }}>Search</Box>

        <Box
          onClick={exportDisabled ? undefined : exportCsv}
          title={exportDisabled ? "No data to export" : "Export current view to CSV"}
          sx={{
            px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
            userSelect: "none",
            border: "0.5px solid",
            borderColor: exportDisabled ? "divider" : BLUE,
            background: "transparent",
            color: exportDisabled ? "text.disabled" : BLUE,
            fontWeight: 500,
            cursor: exportDisabled ? "default" : "pointer",
            opacity: exportDisabled ? 0.5 : 1,
            alignSelf: "flex-end", mb: "1px",
            display: "inline-flex", alignItems: "center", gap: "6px",
            transition: "all .15s",
            "&:hover": exportDisabled ? {} : { background: "rgba(55,138,221,0.06)" },
          }}
        >
          <span style={{ fontSize: 14, lineHeight: 1 }}>↓</span>
          Export CSV
        </Box>

        <Box sx={{
          display: "flex", ml: "auto",
          border: "0.5px solid", borderColor: "divider",
          borderRadius: "20px", overflow: "hidden",
          alignSelf: "flex-end",
        }}>
          {["Texts", "Emails"].map(ch => (
            <Box key={ch} onClick={() => { setConversationTimeType(ch); load(startDate, endDate, ch); }} sx={{
              px: "14px", py: "5px", fontSize: 13,
              fontWeight:  conversationTimeType === ch ? 500 : 400,
              cursor: "pointer", userSelect: "none",
              background:  conversationTimeType === ch ? BLUE : "transparent",
              color:       conversationTimeType === ch ? "#fff" : "text.secondary",
              transition:  "all .15s",
            }}>{ch}</Box>
          ))}
        </Box>
      </Box>

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 3 }}>
          <CircularProgress size={20} /> Loading…
        </Box>
      )}

      {!loading && data.length === 0 && !error && (
        <Typography sx={{ p: 3, color: "text.secondary", textAlign: "center" }}>
          No data for selected range.
        </Typography>
      )}

      {!loading && data.length > 0 && (
        <>
          {/* ── SUMMARY KPIs ─────────────────────────────────── */}
          {/* Averages → whole range. Min/Max → the day that produced them. */}
          <SectionTitle>Summary</SectionTitle>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
            <KpiCard
              label="Avg first response"
              value={`${safeNum(kpis.avgFirstResp)} m`}
              sub={`${safeNum(kpis.avgFirstResp / 60)} hrs`}
              color={BLUE}
              onClick={() => openDrill({
                metric: { label: "Avg first response", value: `${safeNum(kpis.avgFirstResp)}m`, color: BLUE },
              })}
            />
            <KpiCard
              label="Avg conversation"
              value={`${safeNum(kpis.avgConv)} m`}
              sub={`${safeNum(kpis.avgConv / 60)} hrs`}
              color={GREEN}
              onClick={() => openDrill({
                metric: { label: "Avg conversation", value: `${safeNum(kpis.avgConv)}m`, color: GREEN },
              })}
            />
            <KpiCard
              label="Min first response"
              value={`${safeNum(kpis.minFirst)} m`}
              color={AMBER}
              onClick={kpis.minFirstRow && (() => openDrill({
                date: kpis.minFirstRow.ConversationDate,
                metric: { label: "Min first response", value: `${safeNum(kpis.minFirst)}m`, color: AMBER },
              }))}
            />
            <KpiCard
              label="Max first response"
              value={`${safeNum(kpis.maxFirst)} m`}
              color={PINK}
              onClick={kpis.maxFirstRow && (() => openDrill({
                date: kpis.maxFirstRow.ConversationDate,
                metric: { label: "Max first response", value: `${safeNum(kpis.maxFirst)}m`, color: PINK },
              }))}
            />
            <KpiCard
              label="Min conversation"
              value={`${safeNum(kpis.minConv)} m`}
              color={AMBER}
              onClick={kpis.minConvRow && (() => openDrill({
                date: kpis.minConvRow.ConversationDate,
                metric: { label: "Min conversation", value: `${safeNum(kpis.minConv)}m`, color: AMBER },
              }))}
            />
            <KpiCard
              label="Max conversation"
              value={`${safeNum(kpis.maxConv)} m`}
              color={PINK}
              onClick={kpis.maxConvRow && (() => openDrill({
                date: kpis.maxConvRow.ConversationDate,
                metric: { label: "Max conversation", value: `${safeNum(kpis.maxConv)}m`, color: PINK },
              }))}
            />
          </Box>

          {/* ── CHARTS ──────────────────────────────────────── */}
          <SectionTitle>Avg conversation time over time</SectionTitle>

          <ChartCard height={220}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="date" tick={axisStyle} />
                <YAxis tick={axisStyle} tickFormatter={v => `${v}m`} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v, n) => [`${Number(v).toFixed(2)} mins`, n]}
                />
                <Legend iconSize={10} iconType="square" wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="avgFirstMins" name="Avg first response"
                  stroke={BLUE}   dot={{ r: 3 }} strokeWidth={2} />
                <Line type="monotone" dataKey="avgConvMins"  name="Avg conversation"
                  stroke={GREEN}  dot={{ r: 3 }} strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>

          <SectionTitle>Min / max first response time</SectionTitle>

          <ChartCard height={200}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="date" tick={axisStyle} />
                <YAxis tick={axisStyle} tickFormatter={v => `${v}m`} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v, n) => [`${Number(v).toFixed(2)} mins`, n]}
                />
                <Legend iconSize={10} iconType="square" wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="minFirstMins" name="Min first response"
                  stroke={AMBER}  dot={{ r: 3 }} strokeWidth={2} />
                <Line type="monotone" dataKey="maxFirstMins" name="Max first response"
                  stroke={PINK}   dot={{ r: 3 }} strokeWidth={2} />
                <Line type="monotone" dataKey="minConvMins"  name="Min conversation"
                  stroke={PURPLE} dot={{ r: 3 }} strokeWidth={2} strokeDasharray="4 2" />
                <Line type="monotone" dataKey="maxConvMins"  name="Max conversation"
                  stroke={GREEN}  dot={{ r: 3 }} strokeWidth={2} strokeDasharray="4 2" />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* ── DATA TABLE ────────────────────────────────────── */}
          <SectionTitle>Detail</SectionTitle>

          <Box sx={{
            border: "0.5px solid", borderColor: "divider",
            borderRadius: 2, overflow: "hidden",
          }}>
            {/* Header */}
            <Box sx={{
              display: "grid",
              gridTemplateColumns: COLS,
              background: "#f7f7f7",
              borderBottom: "0.5px solid", borderColor: "divider",
              px: 2, py: 1,
              fontSize: 11, fontWeight: 500,
              color: "text.secondary",
              textTransform: "uppercase", letterSpacing: "0.05em",
              alignItems: "center",
            }}>
              {HEADERS.map((h, i) => (
                <Box key={i} sx={{ textAlign: i <= 1 ? "left" : "right" }}>{h}</Box>
              ))}
            </Box>

            {/* Rows */}
            {data.slice(0, 100).map((row, i) => (
              <Box key={i} sx={{
                display: "grid",
                gridTemplateColumns: COLS,
                px: 2, py: "6px",
                fontSize: 12,
                borderBottom: "0.5px solid", borderColor: "divider",
                background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                "&:hover": { background: "rgba(55,138,221,0.06)" },
                alignItems: "center",
              }}>
                {/* drill icon — all conversations on this date */}
                <Box>
                  <IconButton
                    size="small"
                    onClick={() => openDrill({ date: row.ConversationDate })}
                    sx={{ width: 24, height: 24 }}
                    title="View conversations for this date"
                  >
                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>

                <Box>{fmtDate(row.ConversationDate)}</Box>
                <Box sx={{ textAlign: "right", color: BLUE,   fontWeight: 500 }}>
                  {safeNum(row.AvgFirstResponseInMins)}
                </Box>
                <Box sx={{ textAlign: "right", color: BLUE }}>
                  {safeNum(row.AvgFirstResponseInHrs)}
                </Box>
                <Box sx={{ textAlign: "right", color: GREEN,  fontWeight: 500 }}>
                  {safeNum(row.AvgConversationInMins)}
                </Box>
                <Box sx={{ textAlign: "right", color: GREEN }}>
                  {safeNum(row.AvgConversationInHrs)}
                </Box>
                <Box sx={{ textAlign: "right", color: AMBER }}>
                  {safeNum(row.MinFirstResponseInMins)}
                </Box>
                <Box sx={{ textAlign: "right", color: PINK }}>
                  {safeNum(row.MaxFirstResponseInMins)}
                </Box>
                <Box sx={{ textAlign: "right", color: AMBER }}>
                  {safeNum(row.MinConversationInMins)}
                </Box>
                <Box sx={{ textAlign: "right", color: PINK }}>
                  {safeNum(row.MaxConversationInMins)}
                </Box>
              </Box>
            ))}
          </Box>
        </>
      )}

      {/* Drill-down popup */}
      {drill && (
        <ConversationDrillDownDialog
          open={!!drill}
          onClose={() => setDrill(null)}
          {...drill}
        />
      )}
    </Box>
  );
};

export default ConversationTime;